import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { Button, Chip, CircularProgress, IconButton, InputAdornment, OutlinedInput, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import Navigator from "./components/Navigation";
import { Footer } from "./components/Footer";
import { useMetaTags } from "./utils/useMetaTags";
import {
  formatTimestampForDisplay,
  readLastSeenAt,
  saveLastSeenAt,
  type PageViewLogRow,
} from "./utils/pageViewLocalLog";
import "./style.css";
import "./font.css";

const API_URL = process.env.REACT_APP_PAGE_VIEW_API_URL ?? "";
const ALIAS_STORAGE_KEY = "lynsight_device_aliases";

function readAliases(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(ALIAS_STORAGE_KEY) ?? "{}") as Record<string, string>;
  } catch { return {}; }
}

function saveAliases(aliases: Record<string, string>): void {
  localStorage.setItem(ALIAS_STORAGE_KEY, JSON.stringify(aliases));
}

const EVENT_LABELS: Record<string, { label: string; color: "default" | "primary" | "success" | "warning" }> = {
  pageview: { label: "방문", color: "default" },
  protected_view: { label: "잠금화면", color: "warning" },
  content_unlocked: { label: "콘텐츠 열람", color: "success" },
};

function EventChip({ event }: { event?: string }) {
  const cfg = event ? EVENT_LABELS[event] : undefined;
  if (!cfg) {
    return <Chip label={event ?? "방문"} size="small" />;
  }
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

function PageViewLog(props: {
  activeNav: number;
  setActiveNav: Dispatch<SetStateAction<number>>;
}) {
  useMetaTags({
    title: "Page views - Lynkim",
    description: "Site page view log",
    image: "/ogimage.png",
  });

  const [lastSeenAt] = useState<string | null>(() => readLastSeenAt());
  const [rows, setRows] = useState<PageViewLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceFilter, setDeviceFilter] = useState<string | null>(null);
  const [aliases, setAliases] = useState<Record<string, string>>(() => readAliases());
  const [editingDevice, setEditingDevice] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRows = useCallback(async () => {
    if (!API_URL) return;
    try {
      const res = await fetch(API_URL, { mode: "cors", credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { items: PageViewLogRow[] };
      setRows(data.items ?? []);
      setError(null);
    } catch (e) {
      setError("기록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    saveLastSeenAt();
    void fetchRows();
    intervalRef.current = setInterval(() => { void fetchRows(); }, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchRows]);

  const isNew = (timestampUtc: string): boolean => {
    if (!lastSeenAt) return true;
    return timestampUtc > lastSeenAt;
  };

  // deviceId 기준으로 그룹핑 (없으면 device UA 문자열로 fallback)
  const getKey = (r: { deviceId?: string; device?: string }) => r.deviceId ?? r.device ?? "—";

  const filteredRows = deviceFilter ? rows.filter((r) => getKey(r) === deviceFilter) : rows;

  // 각 deviceId별 카운트 + 대표 UA 문자열
  const deviceGroups = rows.reduce<Record<string, { count: number; device: string }>>((acc, r) => {
    const key = getKey(r);
    if (!acc[key]) acc[key] = { count: 0, device: r.device ?? key };
    acc[key].count += 1;
    return acc;
  }, {});

  const startEdit = (deviceId: string) => {
    setEditingDevice(deviceId);
    setEditingValue(aliases[deviceId] ?? "");
  };

  const commitEdit = () => {
    if (!editingDevice) return;
    const next = { ...aliases };
    if (editingValue.trim()) {
      next[editingDevice] = editingValue.trim();
    } else {
      delete next[editingDevice];
    }
    saveAliases(next);
    setAliases(next);
    setEditingDevice(null);
  };

  const cancelEdit = () => setEditingDevice(null);

  // 별칭 → UA 문자열 → deviceId 순으로 표시
  const displayName = (key: string, uaString: string) => aliases[key] ?? uaString;

  const handleDeleteAll = async () => {
    if (!window.confirm("모든 기록을 삭제할까요? 설정한 별칭은 유지됩니다.")) return;
    try {
      const res = await fetch(API_URL, { method: "DELETE", mode: "cors", credentials: "omit" });
      if (!res.ok) throw new Error();
      setRows([]);
      setDeviceFilter(null);
    } catch {
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  return (
    <>
      <Navigator activeNav={props.activeNav} setActiveNav={props.setActiveNav} />
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "120px 24px 80px" }}>
        <Typography component="h1" variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
          페이지 조회 기록
        </Typography>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <Button variant="outlined" size="small" onClick={() => { void fetchRows(); }}>
            새로고침
          </Button>
          <Button variant="outlined" color="error" size="small" onClick={() => { void handleDeleteAll(); }}>
            모든 기록 삭제
          </Button>
        </div>
        {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}
        {error && <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>}
        {!loading && Object.keys(deviceGroups).length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, alignItems: "center" }}>
            <Chip
              label={`전체 ${rows.length}`}
              size="small"
              onClick={() => setDeviceFilter(null)}
              color={deviceFilter === null ? "primary" : "default"}
              variant={deviceFilter === null ? "filled" : "outlined"}
            />
            {Object.entries(deviceGroups).map(([key, { count, device }]) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {editingDevice === key ? (
                  <OutlinedInput
                    autoFocus
                    size="small"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") cancelEdit(); }}
                    placeholder={device}
                    sx={{ fontSize: "0.8rem", height: 28, width: 180 }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={commitEdit} sx={{ fontSize: 12 }}>✓</IconButton>
                        <IconButton size="small" onClick={cancelEdit} sx={{ fontSize: 12 }}>✕</IconButton>
                      </InputAdornment>
                    }
                  />
                ) : (
                  <>
                    <Chip
                      label={`${displayName(key, device)} ${count}`}
                      size="small"
                      onClick={() => setDeviceFilter(deviceFilter === key ? null : key)}
                      color={deviceFilter === key ? "primary" : "default"}
                      variant={deviceFilter === key ? "filled" : "outlined"}
                    />
                    <IconButton size="small" onClick={() => startEdit(key)} sx={{ p: 0.2, fontSize: 11, color: "text.disabled" }}>
                      ✎
                    </IconButton>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid var(--color-overlay-black-08)" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>페이지 URL</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 130 }}>이벤트</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 160 }}>방문자</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 160 }}>위치</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 240 }}>조회 시간</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>아직 기록이 없습니다. 다른 페이지를 방문해 보세요.</TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row) => {
                  const { localKo } = formatTimestampForDisplay(row.timestampUtc);
                  const rowIsNew = isNew(row.timestampUtc);
                  const key = getKey(row);
                  const isFiltered = deviceFilter === key;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: "break-all", display: "flex", alignItems: "center", gap: 1 }}>
                          {rowIsNew && (
                            <Chip label="NEW" size="small" color="error" sx={{ fontWeight: 700, fontSize: "0.65rem", height: 18, flexShrink: 0 }} />
                          )}
                          {row.pageUrl}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <EventChip event={row.event} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={displayName(key, row.device ?? key)}
                          size="small"
                          onClick={() => setDeviceFilter(isFiltered ? null : key)}
                          color={isFiltered ? "primary" : "default"}
                          variant={isFiltered ? "filled" : "outlined"}
                          sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{row.location ?? "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{localKo}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <Footer />
    </>
  );
}

export default PageViewLog;
